import { Paper, Typography, Divider, Box } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useState, useEffect } from 'react';
import SingleValueEditBox from '../SingleValueEditBox';
import { useDispatch, useSelector } from 'react-redux';
import { useMsal } from '@azure/msal-react';
import { updateHotelMarkUpBuyRate, updateHotelMarkUpSellTotal, updateServiceMarkUpBuyRate, updateServiceMarkUpSellTotal } from 'src/store/reservations/slices/ReservationsManagerSlice';
import { RateType, ReservationType, ServiceType } from 'src/utils/constants';
import ErrorView from 'src/components/errors/ErrorView';

const RateDetailsPanel = ({ row }) => {
  const [buyRate, setBuyRate] = useState(row.buyRate);
  const [sellTotal, setSellTotal] = useState(row.netSellTotal);
  const [loading, setLoading] = useState({ buy: false, sell: false });
  const [success, setSuccess] = useState({ buy: false, sell: false });
  const [errorFlags, setErrorFlags] = useState({ buy: false, sell: false });
  const [errorObj, setErrorObj] = useState(null);

  const dispatch = useDispatch();
  const { instance, accounts } = useMsal();
  const { reservationOverview } = useSelector((state) => state.reservationsSummaryReducer);

  useEffect(() => {
    setBuyRate(row.buyRate);
    setSellTotal(row.netSellTotal);
  }, [row.buyRate, row.netSellTotal]);

  // Handle Buy Rate Update
  const handleBuyRateSave = async () => {
    setLoading(prev => ({ ...prev, buy: true }));
    setErrorFlags(prev => ({ ...prev, buy: false }));
    setErrorObj(null);
    setSuccess(prev => ({ ...prev, buy: false }));

    try {
      let requestPayload;

      if (row.type === ReservationType.hotel) {
        // Hotel item (Room/Supplement/Discount)
        requestPayload = {
          ItemReference: row.baseItemTypeData.ItemReference || null,
          RoomId: row.baseData.RoomId || 1,
          UnitTypeId: null,
          RateType: RateType.contractBuy || null,
          TargetType: row.item,
          RateId: row.rateId || null,
          NewValue: buyRate
        };

        await dispatch(
          updateHotelMarkUpBuyRate(
            instance,
            accounts,
            reservationOverview.Id,
            row.baseItemTypeData.PropertyId,
            requestPayload
          )
        );
      } else {
        // Service item
        requestPayload = {
          ItemReference: row.baseItemTypeData.ItemReference || null,
          // RoomId: null, Service items don't have RoomId, no need to send this to api
          UnitTypeId: row.baseData.ServiceUnitTypeId || null,
          RateType: RateType.contractBuy || null,
          TargetType: row.roomRateType,
          RateId: row.rateId || null,
          NewValue: buyRate
        };

        await dispatch(
          updateServiceMarkUpBuyRate(
            instance,
            accounts,
            reservationOverview.Id,
            row.baseItemTypeData.ServiceId,
            requestPayload
          )
        );
      }

      setSuccess(prev => ({ ...prev, buy: true }));

      // Reset success after a delay
      setTimeout(() => {
        setSuccess(prev => ({ ...prev, buy: false }));
      }, 2000);

    } catch (err) {
      setErrorFlags(prev => ({ ...prev, buy: true }));
      setErrorObj(err);
    } finally {
      setLoading(prev => ({ ...prev, buy: false }));
    }
  };

  // Handle Sell Total Update
  const handleSellTotalSave = async () => {
    setLoading(prev => ({ ...prev, sell: true }));
    setErrorFlags(prev => ({ ...prev, sell: false }));
    setErrorObj(null);
    setSuccess(prev => ({ ...prev, sell: false }));

    try {
      let requestPayload;

      if (row.type === ReservationType.hotel) {
        // Hotel item (Room/Supplement/Discount)
        requestPayload = {
          ItemReference: row.baseItemTypeData.ItemReference || null,
          RoomId: row.baseData.RoomId || 1,
          UnitTypeId: null,
          RateType: RateType.clientNetSell || null,
          TargetType: row.item,
          RateId: row.rateId || null,
          NewValue: sellTotal
        };

        await dispatch(
          updateHotelMarkUpSellTotal(
            instance,
            accounts,
            reservationOverview.Id,
            row.baseItemTypeData.PropertyId,
            requestPayload
          )
        );
      } else {
        // Service item
        requestPayload = {
          ItemReference: row.baseItemTypeData.ItemReference || null,
          // RoomId: null, Service items don't have RoomId, no need to send this to api
          UnitTypeId: row.baseData.ServiceUnitTypeId || null,
          RateType: RateType.clientNetSell || null,
          TargetType: row.roomRateType,
          RateId: row.rateId || null,
          NewValue: sellTotal
        };

        await dispatch(
          updateServiceMarkUpSellTotal(
            instance,
            accounts,
            reservationOverview.Id,
            row.baseItemTypeData.ServiceId,
            requestPayload
          )
        );
      }

      setSuccess(prev => ({ ...prev, sell: true }));

      setTimeout(() => {
        setSuccess(prev => ({ ...prev, sell: false }));
      }, 2000);

    } catch (err) {
      setErrorFlags(prev => ({ ...prev, sell: true }));
      setErrorObj(err);
    } finally {
      setLoading(prev => ({ ...prev, sell: false }));
    }
  };

  const handleClose = () => {
    setErrorObj(null);
    setErrorFlags({ buy: false, sell: false });
  };

  return (
    <Box sx={{ py: 3, px: 2, backgroundColor: '#fafafa' }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2, color: '#1976d2' }}>
        Detailed Rate Information
        {row.type === ReservationType.service ? ' (Service)' : ' (Hotel)'}
      </Typography>

      {errorObj && (
        <ErrorView
          error={errorObj}
          onClose={handleClose}
        />
      )}

      <Grid container spacing={3}>
        {/*Booking Details and Exchange Rates */}
        <Grid size={{ xs: 12, md: 6 }}>
          {/* Booking Details */}
          <Paper elevation={0} sx={{ p: 2, backgroundColor: 'white', borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Booking Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Group:</Typography>
                <Typography variant="body2" fontWeight={600}>{row.group}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Item:</Typography>
                <Typography variant="body2" fontWeight={600}>{row.item}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {row.type === ReservationType.service ? 'Service Type:' : 'Room Rate Type:'}
                </Typography>
                <Typography variant="body2" fontWeight={600}>{row.type === ReservationType.hotel ? row.baseData.RoomType : row.baseData.ServiceType}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Description:</Typography>
                <Typography variant="body2" fontWeight={600}>{row.description}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {row.type === ReservationType.service ? 'Unit Count:' : 'Room Count:'}
                </Typography>
                {row.type === ReservationType.service && row.baseData.ServiceType === ServiceType.single ? (
                  <Typography variant="body2" fontWeight={600}>{row.paxPerUnit}</Typography>
                ) : (
                  <Typography variant="body2" fontWeight={600}>{row.roomCount}</Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Pax per Unit:</Typography>
                {row.type === ReservationType.service && row.baseData.ServiceType === ServiceType.single ? (
                  <Typography variant="body2" fontWeight={600}>{1}</Typography>
                ) : (
                  <Typography variant="body2" fontWeight={600}>{row.paxPerUnit}</Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Charge Type:</Typography>
                <Typography variant="body2" fontWeight={600}>{row.chargeType}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">From Date:</Typography>
                <Typography variant="body2" fontWeight={600}>{row.fromDate}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">To Date:</Typography>
                <Typography variant="body2" fontWeight={600}>{row.toDate}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Quantity:</Typography>
                <Typography variant="body2" fontWeight={600}>{row.qty}</Typography>
              </Box>
            </Box>
          </Paper>

          {/* Exchange Rates */}
          <Paper elevation={0} sx={{ p: 2, backgroundColor: 'white', borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Exchange Rates
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Contract to System Exchange Rate:</Typography>
                <Typography variant="body2" fontWeight={600}>{row.contractToSystemRate}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">System to Client Exchange Rate:</Typography>
                <Typography variant="body2" fontWeight={600}>{row.systemToClientRate}</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/*Buy Rate, Markup, and Sell Rate */}
        <Grid size={{ xs: 12, md: 6 }}>
          {/* Buy Rate Info */}
          <Paper elevation={0} sx={{ p: 2, backgroundColor: 'white', borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Buy Rate Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Buy Currency:</Typography>
                <Typography variant="body2" fontWeight={600}>{row.buyCurrency}</Typography>
              </Box>

              {/* Editable Buy Rate */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Buy Rate:</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>{row.buyCurrency}</Typography>
                  <SingleValueEditBox
                    title=""
                    value={buyRate}
                    onChange={setBuyRate}
                    onSave={handleBuyRateSave}
                    loading={loading.buy}
                    success={success.buy}
                    error={errorFlags.buy}
                    type="text"
                    titleValueGap={1}
                    isNumeric={true}
                    decimalPlaces={2}
                    allowDecimals={true}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Buy Total (Without Tax):</Typography>
                <Typography variant="body2" fontWeight={600}>{row.buyCurrency} {row.buyTotalWithoutTax}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Buy Tax:</Typography>
                <Typography variant="body2" fontWeight={600}>{row.buyCurrency} {row.buyTax}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '2px solid #4caf50' }}>
                <Typography variant="body1" fontWeight={700} color="primary.main">Buy Total (With Tax):</Typography>
                <Typography variant="body1" fontWeight={700} color="primary.main">{row.buyCurrency} {row.buyTotalWithTax}</Typography>
              </Box>
            </Box>
          </Paper>

          {/* Markup Information */}
          <Paper elevation={0} sx={{ p: 2, backgroundColor: 'white', borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Markup Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Markup %:</Typography>
                <Typography variant="body2" fontWeight={600} color="error">{row.markupRate}%</Typography>
              </Box>
            </Box>
          </Paper>

          {/* Sell Rate Info */}
          <Paper elevation={0} sx={{ p: 2, backgroundColor: 'white', borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
              Sell Rate Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Sell Currency:</Typography>
                <Typography variant="body2" fontWeight={600}>{row.sellCurrency}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Buy Total in Sell:</Typography>
                <Typography variant="body2" fontWeight={600}>{row.buyCurrency} {row.buyTotalInSell}</Typography>
              </Box>

              {/* Net Sell Rate (read-only) */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Net Sell (Without Tax):</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>{row.sellCurrency}</Typography>
                  <Typography variant="body2" fontWeight={600}>{row.netSellRate}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Net Sell Tax:</Typography>
                <Typography variant="body2" fontWeight={600}>{row.sellCurrency} {row.netSellTax}</Typography>
              </Box>

              {/* Editable Sell Total */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '2px solid #4caf50' }}>
                <Typography variant="body1" fontWeight={700} color="primary.main">Net Sell Total (With Tax):</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body1" fontWeight={700} color="primary.main">{row.sellCurrency}</Typography>
                  <SingleValueEditBox
                    title=""
                    value={sellTotal}
                    onChange={setSellTotal}
                    onSave={handleSellTotalSave}
                    loading={loading.sell}
                    success={success.sell}
                    error={errorFlags.sell}
                    type="text"
                    titleValueGap={1}
                    isNumeric={true}
                    decimalPlaces={2}
                    allowDecimals={true}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RateDetailsPanel;